import { useState, useRef, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import { transcribeAudio } from '@/api/sttAPI'

interface SpeechToTextProps {
  onTranscription: (text: string) => void
  token: string
  disabled?: boolean
}

type RecordingState = 'idle' | 'recording' | 'processing'

function getSupportedMimeType(): string | null {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return null
}

const MAX_RECORDING_MS = 5 * 60 * 1000
const MAX_RECORDING_S = MAX_RECORDING_MS / 1000
const MIN_RECORDING_MS = 1000

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function SpeechToText({
  onTranscription,
  token,
  disabled,
}: SpeechToTextProps) {
  const [state, setState] = useState<RecordingState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [showMore, setShowMore] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [timerAnnouncement, setTimerAnnouncement] = useState('')
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const recordingStartRef = useRef<number | null>(null)
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    return () => {
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current)
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current)
    }
  }, [])

  // Hide button entirely if MediaRecorder is not available (e.g. insecure context)
  if (
    typeof window === 'undefined' ||
    !navigator.mediaDevices ||
    !window.MediaRecorder
  ) {
    return null
  }

  const startRecording = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      if (!mimeType) {
        setError('Audio recording is not supported in this browser.')
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      chunksRef.current = []
      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = async () => {
        if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current)
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current)
        setElapsedSeconds(0)
        setTimerAnnouncement('')
        stream.getTracks().forEach((t) => t.stop())
        const duration = recordingStartRef.current
          ? Date.now() - recordingStartRef.current
          : 0
        if (duration < MIN_RECORDING_MS) {
          setState('idle')
          return
        }
        const blob = new Blob(chunksRef.current, { type: mimeType })
        await sendAudio(blob, mimeType)
      }

      recorder.start()
      recordingStartRef.current = Date.now()
      setElapsedSeconds(0)
      setTimerAnnouncement('Recording started. Up to 5 minutes.')
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((s) => {
          const next = s + 1
          const remaining = MAX_RECORDING_S - next
          if (remaining === 60) setTimerAnnouncement('1 minute remaining')
          else if (remaining === 30)
            setTimerAnnouncement('30 seconds remaining')
          else if (remaining === 10)
            setTimerAnnouncement('10 seconds remaining')
          return next
        })
      }, 1000)
      autoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop()
          setState('processing')
        }
      }, MAX_RECORDING_MS)
      setState('recording')
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone permission denied.')
      } else {
        setError('Could not access microphone.')
      }
    }
  }

  const stopRecording = () => {
    mediaRecorderRef.current?.stop()
    setState('processing')
  }

  const sendAudio = async (blob: Blob, mimeType: string) => {
    try {
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm'
      const text = await transcribeAudio(token, blob, `recording.${ext}`)
      if (text) {
        onTranscription(text)
      }
    } catch {
      setError('Error transcribing audio')
    } finally {
      setState('idle')
    }
  }

  const handleClick = () => {
    if (state === 'idle') startRecording()
    else if (state === 'recording') stopRecording()
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip
          title={
            state === 'idle'
              ? 'Start recording'
              : state === 'recording'
                ? 'Stop recording'
                : 'Processing…'
          }
        >
          <span>
            <IconButton
              disableRipple
              onClick={handleClick}
              disabled={disabled || state === 'processing'}
              size="small"
              color={state === 'recording' ? 'error' : 'default'}
              aria-label={
                state === 'recording' ? 'Stop recording' : 'Start voice input'
              }
            >
              {state === 'processing' ? (
                <CircularProgress size={20} />
              ) : state === 'recording' ? (
                <StopIcon />
              ) : (
                <MicIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          {state === 'recording' ? (
            <Typography
              variant="caption"
              role="timer"
              aria-hidden="true"
              color={
                elapsedSeconds >= MAX_RECORDING_S - 30
                  ? 'error'
                  : 'text.secondary'
              }
            >
              {formatTime(Math.max(0, MAX_RECORDING_S - elapsedSeconds))}{' '}
              remaining
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Click the microphone to record. You can stop and start again to
              add more.
            </Typography>
          )}
          <Button
            variant="text"
            size="small"
            disableRipple
            onClick={() => setShowMore((v) => !v)}
            sx={{
              p: 0,
              minWidth: 0,
              fontSize: '0.75rem',
              textTransform: 'none',
              textDecoration: 'underline',
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
              '&:focus-visible': {
                outline: '2px solid',
                outlineOffset: '2px',
                borderRadius: '2px',
              },
              ml: isMobile ? 0 : 1,
            }}
          >
            {showMore ? 'Tell me less' : 'Tell me more'}
          </Button>
        </Box>
      </Box>
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {timerAnnouncement}
      </Box>
      {showMore && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, ml: '36px' }}
        >
          Your recording will be transcribed to fill in this field for you.
          Recording will automatically stop after 5 minutes. For best results,
          speak clearly and close to your microphone.
        </Typography>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}
